import React, { useState, useEffect} from 'react';
import { number, string } from 'prop-types';
import moment from 'moment';
import { useFormik } from 'formik';

import clsx from 'clsx';

import { withRouter } from 'react-router';
import { connect } from 'react-redux';

import Button from '@material-ui/core/Button';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import Paper from '@material-ui/core/Paper';
import FormControl from '@material-ui/core/FormControl';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';

import BobConfirmButton from '../BobConfirmButton';

import * as BobActions from '../../actions/bobActions';
import * as DashboardActions from '../../actions/dashboardActions';

import DateField from '../FormFields/DateField';
import validationSchema from './BobTimestampFilter.schema';

import classes from './BobTimestampFilter.module.css';

const MAX_RANGE_MESSAGE_1 = 'The maximum range is 4 hours. The start time must be at least 1 hour less than end time.';
const MAX_RANGE_MESSAGE_5 = 'The maximum date range is 24 hours. The start time must be at least 1 hour less than end time.';
const MAX_RANGE_MESSAGE_10 = 'The maximum date range is 3 days. The start time must be at least 1 hour less than end time.';
const MAX_RANGE_MESSAGE_DEFAULT = 'The maximum date range is 31 days. The start time must be at least 1 hour less than end time.';

const DESIRED_INTERVALS = [
    {
        id:       1,
        name:     1,
        maxRange: 240
    },
    {
        id:       5,
        name:     5,
        maxRange: 1440
    },
    {
        id:       10,
        name:     10,
        maxRange: 4320
    },
    {
        id:       15,
        name:     15,
        maxRange: 44640
    },
    {
        id:       30,
        name:     30,
        maxRange: 44640
    },
    {
        id:       60,
        name:     60,
        maxRange: 44640
    }
];

const DESIRED_INTERVALS_24_HOURS  = [
    {
        id:       5,
        name:     5,
        maxRange: 1440
    },
    {
        id:       10,
        name:     10,
        maxRange: 4320
    },
    {
        id:       15,
        name:     15,
        maxRange: 44640
    },
    {
        id:       30,
        name:     30,
        maxRange: 44640
    },
    {
        id:       60,
        name:     60,
        maxRange: 44640
    }
];

const DESIRED_INTERVALS_72_HOURS = [
    {
        id:       10,
        name:     10,
        maxRange: 4320
    },
    {
        id:       15,
        name:     15,
        maxRange: 44640
    },
    {
        id:       30,
        name:     30,
        maxRange: 44640
    },
    {
        id:       60,
        name:     60,
        maxRange: 44640
    }
];

const DESIRED_INTERVALS_31_DAYS = [
    {
        id:       15,
        name:     15,
        maxRange: 44640
    },
    {
        id:       30,
        name:     30,
        maxRange: 44640
    },
    {
        id:       60,
        name:     60,
        maxRange: 44640
    }
];

const BobTimeStampFilter = ({
    application,
    className,
    dateFormat,
    dashboardEndTimeStamp,
    dashboardIsCurrentTimeStamp,
    dashboardCurrentTimeStamp,
    dashboardStartTimeStamp,
    dashboardTimePeriod,
    desiredInterval,
    maxDate,
    minDate,
    disableIntervalSelector,
    showTimeSelect,
    setDashboardStartTimestamp,
    setDashboardEndTimestamp,
    setDashboardIsCurrentTimestamp,
    setDashboardTimePeriod,
    setDashboardCurrentTimestamp,
    setDesiredInterval,
    setFaultData,
    timeFormat,
    timeIntervals
}) => {

    const [menuOpen, setMenuOpen] = useState(false);
    const [isCurrentTime, setIsCurrentTime] = useState(dashboardIsCurrentTimeStamp);
    const [interval, setInterval] = useState(desiredInterval);
    const [timePeriod, setTimePeriod] = useState(dashboardTimePeriod);
    const [maxRange, setMaxRange] = useState(20160);
    const [maxRangeMessage, setMaxRangeMessage] = useState(MAX_RANGE_MESSAGE_DEFAULT);
    const [desiredIntervals, setDesiredIntervals] = useState([...DESIRED_INTERVALS_24_HOURS]);

    useEffect(() => {
        setTimePeriod(dashboardTimePeriod);
    }, [dashboardTimePeriod]);

    const handleToggle = () => {
        setMenuOpen(!menuOpen);
        setIsCurrentTime(dashboardIsCurrentTimeStamp);
    };

    const handleClickAway = () => {
        setMenuOpen(false);
    };

    const { errors, touched, values, ...form } = useFormik({
        enableReinitialize: true,
        initialTouched:     {
            endTimestamp:   true,
            startTimestamp: true
        },
        initialValues: {
            endTimestamp:   dashboardEndTimeStamp,
            startTimestamp: dashboardStartTimeStamp
        },
        validateOnBlur: false,
        validationSchema
    });

    /**
     * Modifies the appropriate timestamp value based on the value of the
     * changed requisite date or time input.
     */
    function handleChange(name) {
        return (value, event) => {
            let parsedValue = null;
            if (value) {
                parsedValue = moment(value);
                // endtime should not be a future time. 
                if ( name === 'endTimestamp') {
                    const currentTimeStamp = moment().local();
                    if (parsedValue > currentTimeStamp) {
                        parsedValue = moment(currentTimeStamp);
                    }
                }
                form.setFieldValue(name, parsedValue, true);
            }
            form.setFieldTouched(name, true, false);
            onFilterChange(name, parsedValue, value);
        };
    }

    const onFilterChange = async (filterName, value) => {
        let diffMinutes = 0; 
        if (filterName === 'startTimestamp') {
            if (value) {
                let start = value.local();
                if (moment(start).isSameOrAfter(moment(values.endTimestamp))) {
                    start = moment(values.endTimestamp).subtract(1, 'h');
                }
                if (moment(values.endTimestamp).local().diff(start, 'minutes') < 60) {
                    if (moment(start).isAfter(moment().local()) || moment(values.endTimestamp).add(maxRange).local().isAfter(moment().local())) {
                        start = moment().local().subtract(1, 'h');
                        const end = moment().local();
                        form.setFieldValue('endTimestamp', end, true);
                    }
                    else {
                        start = moment(values.endTimestamp).local().subtract(1, 'h');
                    }
                }
                else if (moment(values.endTimestamp).local().diff(start, 'minutes') > maxRange) {
                    const end = moment(start).add(maxRange, 'm').local();
                    form.setFieldValue('endTimestamp', end, true);
                }
                form.setFieldValue('startTimestamp', start, true);
                diffMinutes = values.endTimestamp.diff(start, 'minutes');
            }
        } else if (filterName === 'endTimestamp') {
            if (value) {
                let tempEnd = value.local();
                if (moment(tempEnd).isAfter(moment().local())) {
                    tempEnd = moment().local();
                }
                form.setFieldValue('endTimestamp', tempEnd, true);
                if (moment(tempEnd).local().diff(values.startTimestamp, 'minutes') > maxRange) {
                    const start = moment(tempEnd).subtract(maxRange, 'm').local();
                    form.setFieldValue('startTimestamp', start, true);
                }
                diffMinutes =tempEnd.diff(values.startTimestamp, 'minutes');
            }
        }
        
        if (diffMinutes <= 240) {
            setDesiredIntervals([...DESIRED_INTERVALS]);
        } else if (diffMinutes <= 1440) {
            setDesiredIntervals([...DESIRED_INTERVALS_24_HOURS]);
        } else if (diffMinutes <= 4320) {
            setDesiredIntervals([...DESIRED_INTERVALS_72_HOURS]);
        } else {
            setDesiredIntervals([...DESIRED_INTERVALS_31_DAYS]);
        }
    };

    useEffect(() => {
        if (!isCurrentTime && dashboardIsCurrentTimeStamp) {
            const subtractMinutes = maxRange >= 1440 ? 1440 : maxRange;
            const endTimeStamp = moment().local();
            const startTimeStamp = interval >= 5 ?
                moment().set('minute', Math.floor(moment().minute() / interval) * interval).subtract(subtractMinutes, 'm').local()
                : moment().subtract(maxRange, 'm').local();

            form.setFieldValue('startTimestamp', startTimeStamp);
            form.setFieldValue('endTimestamp', endTimeStamp);
        }
    },[isCurrentTime]);

    useEffect(() => {
        if (desiredInterval) {
            const desiredIntervals = DESIRED_INTERVALS.find(di => di.id === desiredInterval);
            setMaxRange(desiredIntervals.maxRange);
            switch (desiredIntervals.id) {
                case 1:
                    setMaxRangeMessage(MAX_RANGE_MESSAGE_1);
                    break;
                case 5:
                    setMaxRangeMessage(MAX_RANGE_MESSAGE_5);
                    break;
                case 10:
                    setMaxRangeMessage(MAX_RANGE_MESSAGE_10);
                    break;
                default:
                    setMaxRangeMessage(MAX_RANGE_MESSAGE_DEFAULT);
                    break;
            }
            setInterval(desiredInterval);
        }
        else {
            setMaxRange(20160);
            setMaxRangeMessage(MAX_RANGE_MESSAGE_DEFAULT);
        }
    }, [desiredInterval]);

    const handleGo = () => {
        if (timePeriod === 'Current') {
            const endTimeStamp = moment().local();
            const startTimeStamp = moment().local();

            setDashboardStartTimestamp(startTimeStamp);
            setDashboardEndTimestamp(endTimeStamp);
            setDashboardCurrentTimestamp(moment().local());
        } else if (timePeriod === 'Previous') {
            const endTimestamp = moment().local();
            const startTimestamp = interval >= 5 ?
                moment().set('minute', Math.floor(moment().minute() / interval) * interval).subtract(1, 'd').local()
                : moment().subtract(maxRange, 'm').local();

            setDashboardStartTimestamp(startTimestamp);
            setDashboardEndTimestamp(endTimestamp);
        } else if (timePeriod === 'Previous7') {
            const endTimestamp = moment().local();
            const startTimestamp = interval >= 5 ?
                moment().set('minute', Math.floor(moment().minute() / interval) * interval).subtract(7, 'd').local()
                : moment().subtract(maxRange, 'm').local();

            setDashboardStartTimestamp(startTimestamp);
            setDashboardEndTimestamp(endTimestamp);
        } else if (timePeriod === 'Previous14') {
            const endTimestamp = moment().local();
            const startTimestamp = interval >= 5 ?
                moment().set('minute', Math.floor(moment().minute() / interval) * interval).subtract(14, 'd').local()
                : moment().subtract(maxRange, 'm').local();

            setDashboardStartTimestamp(startTimestamp);
            setDashboardEndTimestamp(endTimestamp);
        } else {
            setDashboardStartTimestamp(values.startTimestamp);
            setDashboardEndTimestamp(values.endTimestamp);
        }

        setDashboardTimePeriod(timePeriod);
        setDesiredInterval(interval);
        setDashboardIsCurrentTimestamp(isCurrentTime);
        setFaultData({});
        handleToggle();
    };

    const handleIntervalChange = event => {
        const interval = event.target.value;
        const desiredIntervals = DESIRED_INTERVALS.find(di => di.id === interval);
        if (desiredIntervals) {
            setMaxRange(desiredIntervals.maxRange);
            switch (desiredIntervals.id) {
                case 1:
                    setMaxRangeMessage(MAX_RANGE_MESSAGE_1);
                    break;
                case 5:
                    setMaxRangeMessage(MAX_RANGE_MESSAGE_5);
                    break;
                case 10:
                    setMaxRangeMessage(MAX_RANGE_MESSAGE_10);
                    break;
                default:
                    setMaxRangeMessage(MAX_RANGE_MESSAGE_DEFAULT);
                    break;
            }
        }
        else {
            setMaxRange(20160);
            setMaxRangeMessage(MAX_RANGE_MESSAGE_DEFAULT);
        }

        let start = moment(values.endTimeStamp).subtract(24, 'h').set('minute', Math.floor(moment().minute() / interval) * interval).local();
        if (interval === 1) {
            start = moment(values.endTimestamp).subtract(4, 'h').local();
        }
        form.setFieldValue('startTimestamp', start);
        setInterval(interval);
    };

    const handleTimePeriodChange = (event) => {
        setTimePeriod(event.target.value);
        if (event.target.value === 'Current') {
            setIsCurrentTime(true);
        } else {
            let start = moment(values.endTimeStamp).subtract(24, 'h').set('minute', Math.floor(moment().minute() / interval) * interval).local();
            if (interval === 1) {
                start = moment(values.endTimestamp).subtract(4, 'h').local();
            }
            form.setFieldValue('startTimestamp', start);
            setIsCurrentTime(false);
        }
    };

    return (
        <ClickAwayListener
            mouseEvent={'onMouseUp'}
            onClickAway={() => handleClickAway()}
        >
            <div>
                <React.Fragment>
                    <React.Fragment>
                        <label className={classes.labelText}>
                            { dashboardIsCurrentTimeStamp ? 
                                `Date: As of ${ dashboardCurrentTimeStamp ? moment(dashboardCurrentTimeStamp).format('YYYY-MM-DD h:mm A') : ''}`
                                :
                                `From ${moment(dashboardStartTimeStamp).format('YYYY-MM-DD h:mm A')} To ${moment(dashboardEndTimeStamp).format('YYYY-MM-DD h:mm A')}`
                            }
                        </label>
                        <Button
                            onClick={handleToggle}
                            className={classes.datePickerButton}
                        >
                            <i className="fas fa-calendar-alt" />
                        </Button>
                    </React.Fragment>
                    { menuOpen ? 
                        <Paper className={ timePeriod !== 'Custom' ? classes.containerWithoutDatepicker : classes.datePickerContainer}>
                            <div className={timePeriod !== 'Custom' ? classes.headerAlignment : classes.header}>
                                <FormControl className={classes.TimePeriodContainer}>
                                    {
                                        application === 'energy' ?
                                            <Select
                                                labelId="timePeriodLabel"
                                                id="timePeriod-select"
                                                value={timePeriod}
                                                onChange={handleTimePeriodChange}
                                            >
                                                <MenuItem value={'Previous'}>Previous {maxRange === 240 ? 4 : 24} Hours</MenuItem>
                                                <MenuItem value={'Previous7'}>Previous 7 Days</MenuItem>
                                                <MenuItem value={'Previous14'}>Previous 14 Days</MenuItem>
                                                <MenuItem value={'Custom'}>Custom</MenuItem>
                                            </Select>
                                            :
                                            <Select
                                                labelId="timePeriodLabel"
                                                id="timePeriod-select"
                                                value={timePeriod}
                                                onChange={handleTimePeriodChange}
                                            >
                                                <MenuItem value={'Current'}>Current</MenuItem>
                                                <MenuItem value={'Previous'}>Previous {maxRange === 240 ? 4 : 24} Hours</MenuItem>
                                                <MenuItem value={'Custom'}>Custom</MenuItem>
                                            </Select>
                                    }
                                </FormControl>
                                { !disableIntervalSelector ?
                                    <div className={timePeriod === 'Custom' ? classes.IntervalSelect : ''}>
                                        <label className={classes.IntervalLabel}>{'Interval'}</label>
                                        <Select
                                            value={interval}
                                            onChange={(e) => handleIntervalChange(e)}
                                            className={classes.select}
                                            disabled={disableIntervalSelector}
                                            inputProps={{
                                                name: 'desiredInterval',
                                                id:   'desiredInterval',
                                            }}
                                        >
                                            {
                                                desiredIntervals.map(type =>
                                                    <MenuItem
                                                        key={type.id}
                                                        value={type.id}
                                                    >
                                                        {type.name}
                                                    </MenuItem>
                                                )
                                            }
                                        </Select>
                                    </div>
                                    : null
                                }
                                {timePeriod !== 'Custom' ?
                                    <div className={classes.ButtonContainer}>
                                        <BobConfirmButton
                                            label={'Go'}
                                            onClick={handleGo}
                                        />
                                    </div>
                                    : null
                                }
                            </div>
                            {timePeriod === 'Custom' ?
                                <React.Fragment>
                                    <div className={classes.bobTimestampFilter}>
                                        <div className={classes.startTime}>
                                            <div className={clsx(classes.labelContainer, className)}><label>{'Start '}</label></div>
                                            <DateField
                                                dateFormat={dateFormat}
                                                isClearable={false}
                                                margin="dense"
                                                maxDate={new Date()}
                                                minDate={new Date(2015, 0, 1)}
                                                name="startTimestamp"
                                                onChange={handleChange('startTimestamp')}
                                                showTimeSelect={showTimeSelect}
                                                timeFormat={timeFormat}
                                                timeIntervals={interval < 5 ? 5 : interval}
                                                value={values.startTimestamp.toDate()}
                                                disabled={true}
                                                disableUnderline={true}
                                                InputProps={{
                                                    popperoverride: classes.dateClassName
                                                }}
                                                open={true}
                                            />
                                        </div>
                                        <div className={classes.endTime}>
                                            <div className={clsx(classes.labelContainer, className)}><label>{'End '}</label></div>
                                            <DateField
                                                dateFormat={dateFormat}
                                                isClearable={false}
                                                margin="dense"
                                                maxDate={new Date()}
                                                minDate={new Date(2015, 0, 1)}
                                                name="endTimestamp"
                                                onChange={handleChange('endTimestamp')}
                                                showTimeSelect={showTimeSelect}
                                                timeFormat={timeFormat}
                                                timeIntervals={interval < 5 ? 5 : interval}
                                                touched={touched.endTimestamp}
                                                value={values.endTimestamp.toDate()}
                                                disabled={true}
                                                disableUnderline={true}
                                                InputProps={{
                                                    popperoverride: classes.dateClassName
                                                }}
                                                open={true}
                                            />
                                        </div>
                                    </div>
                                </React.Fragment>
                                : 
                                null
                            }
                            {timePeriod === 'Custom' ?
                                <div className={classes.footer}>
                                    <p className={clsx(classes.infoText, errors.endTimestamp ? classes.error : '')}>{maxRangeMessage}</p>
                                    <div className={classes.ButtonContainer}>
                                        <BobConfirmButton
                                            label={'Go'}
                                            disabled={timePeriod === 'Custom' && errors.endTimestamp}
                                            onClick={handleGo}
                                        />
                                    </div> 
                                </div>
                                : null
                            }
                        </Paper>
                        : null
                    }
                </React.Fragment>
            </div>
        </ClickAwayListener>
    );
};

BobTimeStampFilter.defaultProps = {
    dateFormat:              'yyyy-MM-dd h:mm a',
    showTimeSelect:          true,
    timeFormat:              'h:mm a',
    timeIntervals:           15,
    disableIntervalSelector: false
};

BobTimeStampFilter.propTypes = {
    dateFormat:    string,
    timeFormat:    string,
    timeIntervals: number
};

const mapDispatchToProps = {
    ...BobActions,
    ...DashboardActions
};

const mapStateToProps = state => {
    return {
        dashboardEndTimeStamp:       state.dashboard.dashboardEndTimeStamp,
        dashboardIsCurrentTimeStamp: state.dashboard.dashboardIsCurrentTimeStamp,
        dashboardCurrentTimeStamp:   state.dashboard.dashboardCurrentTimeStamp,
        dashboardStartTimeStamp:     state.dashboard.dashboardStartTimeStamp,
        dashboardTimePeriod:         state.dashboard.dashboardTimePeriod,
        desiredInterval:             state.dashboard.desiredInterval,
    };
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(BobTimeStampFilter));
